import { inject, Injectable } from '@angular/core';
import { GenerateContentPdfService } from 'src/app/@shared/components/services/files/generate-content-pdf.service';
import { PdfGeneratorService } from 'src/app/@shared/components/pdf-generator/pdf-generator.service';
import { PdfConfiguration } from 'src/app/@shared/interfaces/pdf.interface';
import { GenerateDianContentPdfService } from './generate-dian-content-pdf.service';
import { firstValueFrom } from 'rxjs';
import { IContFact } from 'src/app/@interfaces/contfac.interface';
import {
  InvoiceStatus,
  InvoiceStatusDescription,
} from 'src/app/@enums/pdf/invoice-status.enum';
import { TranslateService } from '@ngx-translate/core';
import { IQuota } from 'src/app/@interfaces/quotation.interface';
import { IsoToLocalDateTimeMapper } from '@shared-classes';
import {
  PdfElectronicContentService,
  PdfElectronicInfoService,
} from '@shared-services';
import { InvoicePdf } from '@shared-models';
import { FormatInvoiceNumberService } from '@shared-services';

@Injectable({
  providedIn: 'root',
})
export class ElectronicInvoicePdfService {
  private generateContentPdfService = inject(GenerateContentPdfService);
  private pdfGeneratorService = inject(PdfGeneratorService);
  private generateDianContentPdfService = inject(GenerateDianContentPdfService);
  private translate = inject(TranslateService);
  private pdfElectronicInfoService = inject(PdfElectronicInfoService);
  private pdfElectronicContentService = inject(PdfElectronicContentService);
  private formatInvoiceNumber = inject(FormatInvoiceNumberService);

  async createInvoicePdf(object: InvoicePdf) {
    const activeCompanyDetails = await firstValueFrom(
      this.pdfElectronicInfoService.getActiveCompanyDetail(),
    );

    const invoiceStatus = this.getPdfInvoiceStatusInfo(
      object.invoiceCount!,
      object.invoiceObject,
    );
    const electronicInvoice = this.getElectronicInvoiceCode(object);
    const parsedDate = this.getParsedInvoiceDate(object);

    const emitter = this.pdfElectronicInfoService.buildEmitter(
      activeCompanyDetails!,
    );
    const prefix = this.pdfElectronicInfoService.getPrefix(
      activeCompanyDetails!,
      object.invoiceObject.billyInvoice?.voucher!,
    );
    const dian = this.pdfElectronicInfoService.buildDianData(
      object.cufe! || object.invoiceObject.billyInvoice?.cufe! || '',
      prefix,
    );
    const client = this.pdfElectronicInfoService.buildClient(
      object.invoiceObject.cliente,
    );
    const emission = this.pdfElectronicInfoService.buildEmission(
      object.invoiceObject.dueDate,
      electronicInvoice,
      parsedDate,
    );

    const items = this.pdfElectronicInfoService.generatePdfItems(
      object.sales,
      object.invoiceObject,
    );
    const totals = this.pdfElectronicInfoService.buildTotals(object);

    const legal = this.buildLegalText(dian.cufe);
    const logoObj = await this.generateContentPdfService.generatePdfImageLogo(
      object.company,
    );

    const emitterInfo =
      await this.generateDianContentPdfService.createEmitterInfo(
        emitter,
        logoObj,
      );

    const content = this.pdfElectronicContentService.buildContent(
      object.invoiceObject.observations || 'N/A',
      emitterInfo,
      dian,
      client,
      emission,
      items,
      totals,
      invoiceStatus.type,
    );
    const styles = this.pdfElectronicInfoService.buildStyles();

    if (dian.cufe) {
      object.qrUrl = this.pdfElectronicInfoService.generateQr(
        object,
        electronicInvoice,
        parsedDate,
        emitter,
        client,
        totals,
        dian,
      );
    }

    const pdfConfiguration: PdfConfiguration = {
      openPDF: object.open!,
      downloadPDF: false,
      content,
      pageMargins: [40, 160, 40, 80],
      pageSize: 'A4',
      styles,
      header: () => [
        this.generateDianContentPdfService.createHeaderInvoice(
          invoiceStatus.id,
          invoiceStatus.type,
          electronicInvoice,
          object.qrUrl,
        ),
      ],
      footer: () => this.generateDianContentPdfService.createLegalFooter(legal),
    };

    return this.pdfGeneratorService.createPdf(pdfConfiguration);
  }

  private getElectronicInvoiceCode(object: InvoicePdf): string {
    return (
      this.formatInvoiceNumber.formatElectronicNumber(
        object.invoiceObject.billyInvoice?.invoice_number || '',
        object.invoiceObject.billyInvoice?.voucher || 0,
      ) || this.translate.instant('Pending')
    );
  }

  private getParsedInvoiceDate(object: InvoicePdf) {
    return IsoToLocalDateTimeMapper.mapper(
      object.invoiceObject.invoiced_date || object.invoiceObject.createdAt,
    );
  }

  private buildLegalText(cufe: string) {
    return (
      'Esta factura corresponde a un Documento Electrónico tipo Invoice (formato UBL XML), emitida por medio de V1-INSIGHT Software autorizado por la DIAN. Puede validar la autenticidad de esta factura en la página oficial de la DIAN: www.dian.gov.co utilizando el código CUFE: ' +
      cufe
    );
  }

  private getPdfInvoiceStatusInfo(
    invoiceCount: IContFact,
    invoiceObject: IQuota,
  ) {
    let status: { type: string; id: string } = { type: '', id: '' };
    switch (invoiceObject.estado) {
      case InvoiceStatus.Quotation:
        status = {
          type: `${this.translate.instant('Quotation/Order')}`,
          id: invoiceCount.quotation,
        };
        break;
      case InvoiceStatus.Rejected:
        status = {
          type: `${this.translate.instant('Reject')}`,
          id: invoiceCount.quotation,
        };
        break;
      case InvoiceStatus.Delivery:
        status = {
          type: `${this.translate.instant('Delivery')}`,
          id: invoiceCount.delivery,
        };
        break;
      case InvoiceStatus.Invoice:
        status = {
          type: `${this.translate.instant('Electronic Invoice')}`,
          id: invoiceCount.invoice,
        };
        break;
      default:
        status = { type: InvoiceStatusDescription.Unknown, id: '' };
        break;
    }
    return status;
  }
}
